import { useEffect, useState } from "react";
import { LOGO_URL } from "../lib/i18n";

const NAV_LINKS = [
  { id: "about", labelKey: "about" },
  { id: "services", labelKey: "services" },
  { id: "steps", labelKey: "steps" },
];

export const Navbar = ({ lang, setLang, t }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("");

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 24);
      const sections = NAV_LINKS.map((l) => document.getElementById(l.id));
      const fromTop = window.scrollY + 140;
      let current = "";
      sections.forEach((sec) => {
        if (sec && sec.offsetTop <= fromTop) current = sec.id;
      });
      setActive(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setOpen(false);
    }
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-black/70 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 h-20 flex items-center justify-between">
        {/* Logo */}
        <button
          data-testid="navbar-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3 group"
          aria-label="PRESTIGE KEY — Top"
        >
          <img
            src={LOGO_URL}
            alt="Prestige Key"
            className="h-10 w-auto logo-3d"
            style={{ animation: "none" }}
          />
          <span className="hidden sm:block heading-display text-lg tracking-[0.2em] uppercase">
            Prestige <span className="opacity-60">Key</span>
          </span>
        </button>

        {/* Desktop Links */}
        <nav className="hidden md:flex items-center gap-12">
          {NAV_LINKS.map((link) => (
            <button
              key={link.id}
              data-testid={`navlink-${link.id}`}
              onClick={() => goTo(link.id)}
              className={`link-underline text-[11px] uppercase tracking-[0.28em] transition-colors ${
                active === link.id ? "text-white active" : "text-white/70 hover:text-white"
              }`}
            >
              {t.nav[link.labelKey]}
            </button>
          ))}
        </nav>

        {/* Right cluster */}
        <div className="flex items-center gap-4">
          <div
            className="hidden sm:flex items-center text-[11px] uppercase tracking-[0.28em] border border-white/15 rounded-full overflow-hidden"
            data-testid="lang-switcher"
          >
            <button
              data-testid="lang-en"
              onClick={() => setLang("en")}
              className={`px-3 py-1.5 transition-colors ${
                lang === "en" ? "bg-white text-black" : "text-white/70 hover:text-white"
              }`}
            >
              EN
            </button>
            <button
              data-testid="lang-fr"
              onClick={() => setLang("fr")}
              className={`px-3 py-1.5 transition-colors ${
                lang === "fr" ? "bg-white text-black" : "text-white/70 hover:text-white"
              }`}
            >
              FR
            </button>
          </div>

          <button
            data-testid="navbar-mobile-toggle"
            onClick={() => setOpen((s) => !s)}
            className="md:hidden h-10 w-10 inline-flex items-center justify-center border border-white/20 rounded-full"
            aria-label="Menu"
          >
            <span className={`block w-4 h-px bg-white transition-transform ${open ? "translate-y-[3px] rotate-45" : ""}`} />
            <span className={`block w-4 h-px bg-white -mt-1.5 transition-transform ${open ? "-translate-y-[3px] -rotate-45" : ""}`} />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`md:hidden overflow-hidden transition-[max-height] duration-500 ${
          open ? "max-h-96" : "max-h-0"
        } bg-black/95 backdrop-blur-xl border-t border-white/10`}
      >
        <div className="px-6 py-6 flex flex-col gap-4">
          {NAV_LINKS.map((link) => (
            <button
              key={link.id}
              data-testid={`navlink-mobile-${link.id}`}
              onClick={() => goTo(link.id)}
              className="text-left text-sm uppercase tracking-[0.28em] text-white/80 hover:text-white py-2"
            >
              {t.nav[link.labelKey]}
            </button>
          ))}
          <div className="flex items-center gap-2 mt-2 text-[11px] uppercase tracking-[0.28em]">
            <button
              data-testid="lang-en-mobile"
              onClick={() => setLang("en")}
              className={`px-3 py-1.5 rounded-full border ${
                lang === "en" ? "bg-white text-black border-white" : "border-white/20 text-white/70"
              }`}
            >
              EN
            </button>
            <button
              data-testid="lang-fr-mobile"
              onClick={() => setLang("fr")}
              className={`px-3 py-1.5 rounded-full border ${
                lang === "fr" ? "bg-white text-black border-white" : "border-white/20 text-white/70"
              }`}
            >
              FR
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};
